import { useCallback, useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import {
  assignAssessor,
  clearRunBaseline,
  getAssignment,
  getEngagement,
  getRun,
  getRunBaseline,
  listEligibleBaselines,
  listEntities,
  listOrganisations,
  myAuthority,

  listPublishedVersions,
  memberDirectory,
  setRunBaseline,
  transitionRun,
} from "../lib/api";
import type {
  Engagement,
  MemberDirectoryEntry,
  MembershipRole,
  Organisation,
  PublishedVersion,
  Run,
  RunAssignment,
  RunBaseline,
  RunState,
} from "../lib/types";
import { ROLE_LABEL } from "../lib/labels";
import { useBreadcrumb } from "../shell/ContextHeader";

// One assessment run: identity, assignment, lifecycle. What this screen must
// get right (review §3.10, §10.3):
//
//   - the ADR-21 trail is complete here: Client › Engagement › Entity › Run
//   - instrument identity is the human sentence; the hash lives in a
//     technical-details drawer, audit material rather than primary content
//   - state changes happen ONLY through assessment_transition_run - there is
//     no edit control for state, because the database would refuse it anyway

const STATE_STYLE: Record<RunState, { label: string; cls: string }> = {
  draft: { label: "draft", cls: "badge-role" },
  ready_for_review: { label: "ready for review", cls: "badge-role" },
  finalized: { label: "finalized", cls: "badge-active" },
  published: { label: "published", cls: "badge-active" },
  withdrawn: { label: "withdrawn", cls: "badge-archived" },
  superseded: { label: "superseded", cls: "badge-archived" },
};

export function StateBadge({ state }: { state: RunState }) {
  const s = STATE_STYLE[state];
  return <span className={`badge ${s.cls}`}>{s.label}</span>;
}

type State =
  | { kind: "loading" }
  | { kind: "error"; message: string }
  | {
      kind: "ready";
      org: Organisation;
      run: Run;
      engagement: Engagement | null;
      entityName: string;
      version: PublishedVersion | null;
      assignment: RunAssignment | null;
      members: MemberDirectoryEntry[];
      role: MembershipRole | null;
      baseline: RunBaseline | null;
      baselineRun: Run | null;
      eligibleBaselines: Run[];
    };

export function RunOverview() {
  const { orgId, runId } = useParams<{ orgId: string; runId: string }>();
  const { setTrail } = useBreadcrumb();
  const [state, setState] = useState<State>({ kind: "loading" });
  const [actionError, setActionError] = useState<string | null>(null);

  const load = useCallback(async () => {
    if (!orgId || !runId) return;
    try {
      const [orgs, role, run] = await Promise.all([
        listOrganisations(),
        myAuthority(orgId),
        getRun(runId),
      ]);
      const org = orgs.find((o) => o.id === orgId);
      if (!org || !run || run.organisation_id !== orgId) {
        setState({ kind: "error", message: "run not found, or you are not a member" });
        return;
      }
      const [engagement, entities, versions, assignment, members, baseline, eligibleBaselines] =
        await Promise.all([
          getEngagement(run.engagement_id),
          listEntities(orgId),
          listPublishedVersions(),
          getAssignment(runId),
          memberDirectory(orgId),
          getRunBaseline(runId),
          listEligibleBaselines(run),
        ]);
      const baselineRun = baseline ? await getRun(baseline.baseline_run_id) : null;
      const entityName = entities.find((e) => e.id === run.entity_id)?.name ?? "—";
      setState({
        kind: "ready",
        org,
        run,
        engagement,
        entityName,
        version: versions.find((v) => v.id === run.instrument_version_id) ?? null,
        assignment,
        members,
        role,
        baseline,
        baselineRun,
        eligibleBaselines,
      });
      setTrail([
        { label: "Clients & engagements", to: "/clients" },
        { label: org.name, to: `/clients/${org.id}` },
        { label: engagement?.name ?? "Engagement", to: `/clients/${org.id}/engagements/${run.engagement_id}` },
        { label: entityName },
        { label: run.title ?? "Untitled run" },
      ]);
    } catch (e) {
      setState({ kind: "error", message: (e as Error).message });
    }
  }, [orgId, runId, setTrail]);

  useEffect(() => {
    void load();
  }, [load]);

  if (state.kind === "loading") return <p>Loading…</p>;
  if (state.kind === "error")
    return <div className="error-banner">Could not load: {state.message}</div>;

  const { org, run, engagement, entityName, version, assignment, members, role,
          baseline, baselineRun, eligibleBaselines } = state;
  const canWrite = role === "owner" || role === "lead_assessor" || role === "assessor";
  const canPublishAuthority = role === "owner" || role === "lead_assessor";
  const active = run.state === "draft" || run.state === "ready_for_review";

  async function act(fn: () => Promise<unknown>) {
    setActionError(null);
    try {
      await fn();
      await load();
    } catch (e) {
      setActionError((e as Error).message);
    }
  }

  return (
    <>
      <div style={{ display: "flex", alignItems: "baseline", gap: "0.75rem" }}>
        <h1 style={{ marginBottom: 0 }}>{run.title ?? "Untitled run"}</h1>
        <StateBadge state={run.state} />
      </div>
      {/* The main action under the title, the lifecycle after it (UI review slice 2). */}
      <div style={{ display: "flex", gap: "0.6rem", flexWrap: "wrap", alignItems: "center", marginTop: "0.75rem" }}>
        <Link className="btn" to={`/clients/${org.id}/runs/${run.id}/assess`}>
          Open assessment workspace
        </Link>
        {run.state === "draft" && canWrite && (
          <button className="btn btn-secondary" onClick={() => act(() => transitionRun(run.id, "ready_for_review"))}>
            Submit for review
          </button>
        )}
        {run.state === "ready_for_review" && canWrite && (
          <button className="btn btn-secondary" onClick={() => act(() => transitionRun(run.id, "draft"))}>
            Return to draft
          </button>
        )}
        {active && canPublishAuthority && (
          <button
            className="btn btn-danger"
            onClick={() => {
              if (window.confirm("Retire this run? A superseded run stays visible but takes no further work.")) {
                void act(() => transitionRun(run.id, "superseded"));
              }
            }}
          >
            Retire run
          </button>
        )}
        {!active && (
          <span style={{ color: "var(--text-muted)" }}>
            This run is {STATE_STYLE[run.state].label}. Later lifecycle steps
            (finalization, publication) arrive with the scoring workspace.
          </span>
        )}
      </div>
      {actionError && <div className="error-banner" style={{ marginTop: "0.75rem" }}>{actionError}</div>}

      <section className="card" style={{ marginTop: "1rem" }}>
        <h2 style={{ marginTop: 0 }}>Identity</h2>
        <dl style={{ display: "grid", gridTemplateColumns: "max-content 1fr", gap: "0.35rem 1.25rem", margin: 0 }}>
          <dt style={{ color: "var(--text-muted)" }}>Client</dt>
          <dd style={{ margin: 0 }}>{org.name}</dd>
          <dt style={{ color: "var(--text-muted)" }}>Engagement</dt>
          <dd style={{ margin: 0 }}>{engagement?.name ?? "—"}</dd>
          <dt style={{ color: "var(--text-muted)" }}>Assessed entity</dt>
          <dd style={{ margin: 0 }}>{entityName}</dd>
          <dt style={{ color: "var(--text-muted)" }}>Instrument</dt>
          <dd style={{ margin: 0 }}>
            {version
              ? `${version.instruments?.title} — v${version.version}`
              : "(version no longer offered — the run remains bound to it)"}
          </dd>
          <dt style={{ color: "var(--text-muted)" }}>Created</dt>
          <dd style={{ margin: 0 }}>{new Date(run.created_at).toLocaleString()}</dd>
        </dl>
        <p style={{ color: "var(--text-muted)", fontSize: "var(--lf-body-size)", marginBottom: 0 }}>
          This run is locked to the instrument version above and will not change
          if the instrument is updated later.
        </p>
        <details style={{ marginTop: "0.5rem" }}>
          <summary style={{ cursor: "pointer", color: "var(--text-muted)", fontSize: "var(--lf-body-size)" }}>
            Technical details
          </summary>
          <dl style={{ display: "grid", gridTemplateColumns: "max-content 1fr", gap: "0.35rem 1.25rem", margin: "0.5rem 0 0", fontSize: "var(--lf-meta-size)" }}>
            <dt style={{ color: "var(--text-muted)" }}>Run id</dt>
            <dd style={{ margin: 0, fontFamily: "var(--lf-font-mono)" }}>{run.id}</dd>
            <dt style={{ color: "var(--text-muted)" }}>Instrument version id</dt>
            <dd style={{ margin: 0, fontFamily: "var(--lf-font-mono)" }}>{run.instrument_version_id}</dd>
            <dt style={{ color: "var(--text-muted)" }}>Definition hash</dt>
            <dd style={{ margin: 0, fontFamily: "var(--lf-font-mono)", overflowWrap: "anywhere" }}>{run.definition_hash}</dd>
          </dl>
        </details>
      </section>

      <section className="card" style={{ marginTop: "1rem" }}>
        <h2 style={{ marginTop: 0 }}>Assessor</h2>
        <AssignmentControl
          run={run}
          assignment={assignment}
          members={members}
          canWrite={canWrite && active}
          onAssign={(assessorId) =>
            act(() => assignAssessor(run.id, org.id, assessorId, assignment !== null))
          }
        />
      </section>

      <section className="card" style={{ marginTop: "1rem" }}>
        <h2 style={{ marginTop: 0 }}>Assessment</h2>
        {/* A self-assessment tenant (0019 §5) gets the customer's own result:
            the dashboard and the PDF report (dashboard proposal V1.1 §4). The
            consultant screens stay for every other run. */}
        {org.self_assessment === true ? (
          <p style={{ marginTop: 0, display: "flex", gap: "0.6rem", flexWrap: "wrap" }}>
            <Link className="btn" to={`/clients/${org.id}/runs/${run.id}/dashboard`}>
              Open dashboard
            </Link>
            <Link className="btn btn-secondary" to={`/clients/${org.id}/runs/${run.id}/client-report?print=1`} target="_blank" rel="noopener">
              Download PDF report
            </Link>
          </p>
        ) : (
        <p style={{ marginTop: 0, display: "flex", gap: "0.6rem", flexWrap: "wrap" }}>
          <Link className="btn btn-secondary" to={`/clients/${org.id}/runs/${run.id}/results`}>
            Evaluation results
          </Link>
          <Link className="btn btn-secondary" to={`/clients/${org.id}/runs/${run.id}/register`}>
            Findings &amp; actions
          </Link>
          <Link className="btn btn-secondary" to={`/clients/${org.id}/runs/${run.id}/report`}>
            Working report
          </Link>
        </p>
        )}
      </section>

      <section className="card" style={{ marginTop: "1rem" }}>
        <h2 style={{ marginTop: 0 }}>Reassessment</h2>
        <ReassessmentCard
          org={org}
          run={run}
          baseline={baseline}
          baselineRun={baselineRun}
          eligible={eligibleBaselines}
          canEdit={canWrite && active}
          onSet={(baselineRunId) => act(() => setRunBaseline(run.id, baselineRunId))}
          onClear={() => act(() => clearRunBaseline(run.id))}
        />
      </section>

    </>
  );
}

// The reassessment card mirrors the database's rules without being the
// boundary (0015): the picker offers same-entity finalized/published runs
// created before this one; the set/replace/clear affordances exist only for
// writers in the draft window; every server refusal renders verbatim above.
function ReassessmentCard({
  org,
  run,
  baseline,
  baselineRun,
  eligible,
  canEdit,
  onSet,
  onClear,
}: {
  org: Organisation;
  run: Run;
  baseline: RunBaseline | null;
  baselineRun: Run | null;
  eligible: Run[];
  canEdit: boolean;
  onSet: (baselineRunId: string) => void;
  onClear: () => void;
}) {
  const [chosen, setChosen] = useState("");
  const [changing, setChanging] = useState(false);
  const options = eligible.filter((r) => r.id !== baseline?.baseline_run_id);

  return (
    <>
      <p style={{ marginTop: 0 }}>
        {baseline ? (
          <>
            Baseline: <strong>{baselineRun?.title ?? baseline.baseline_run_id}</strong>{" "}
            <span style={{ color: "var(--text-muted)", fontSize: "var(--lf-body-size)" }}>
              (created {baselineRun ? new Date(baselineRun.created_at).toLocaleDateString() : "—"};
              selected {new Date(baseline.created_at).toLocaleDateString()})
            </span>
            {/* Rarely changed: one line with "change…" (UI review slice 4). */}
            {canEdit && !changing && (
              <>
                {" · "}
                <button className="btn-link" onClick={() => setChanging(true)}>
                  change…
                </button>
              </>
            )}
          </>
        ) : (
          <span style={{ color: "var(--text-muted)" }}>
            No baseline selected. Bind an earlier finalized run of this entity
            to compare this reassessment against it.
          </span>
        )}
      </p>
      {baseline && (
        <p style={{ marginTop: 0 }}>
          <Link className="btn btn-secondary" to={`/clients/${org.id}/runs/${run.id}/compare`}>
            Open comparison
          </Link>
        </p>
      )}
      {canEdit && (!baseline || changing) && (
        <div style={{ display: "flex", gap: "0.6rem", alignItems: "center", flexWrap: "wrap" }}>
          <select value={chosen} onChange={(e) => setChosen(e.target.value)} aria-label="Choose baseline run">
            <option value="">Choose earlier run…</option>
            {options.map((r) => (
              <option key={r.id} value={r.id}>
                {(r.title ?? "Untitled run") + " — " + r.state + ", " + new Date(r.created_at).toLocaleDateString()}
              </option>
            ))}
          </select>
          <button
            className="btn"
            disabled={chosen === ""}
            onClick={() => {
              onSet(chosen);
              setChosen("");
              setChanging(false);
            }}
          >
            {baseline ? "Replace baseline" : "Set baseline"}
          </button>
          {baseline && (
            <button
              className="btn btn-secondary"
              onClick={() => {
                onClear();
                setChanging(false);
              }}
            >
              Clear baseline
            </button>
          )}
          {baseline && (
            <button className="btn-link" onClick={() => setChanging(false)}>
              Keep
            </button>
          )}
        </div>
      )}
      {!canEdit && baseline && (
        <p style={{ color: "var(--text-muted)", fontSize: "var(--lf-body-size)", margin: 0 }}>
          The baseline is frozen with the run once it leaves active work.
        </p>
      )}
    </>
  );
}

function AssignmentControl({
  run,
  assignment,
  members,
  canWrite,
  onAssign,
}: {
  run: Run;
  assignment: RunAssignment | null;
  members: MemberDirectoryEntry[];
  canWrite: boolean;
  onAssign: (assessorId: string) => void;
}) {
  const [chosen, setChosen] = useState("");
  // A viewer cannot be the assessor (0007 §2) - the picker mirrors the
  // database's answer rather than letting the insert fail.
  const eligible = members.filter((m) => m.member_role !== "viewer");
  const current = members.find((m) => m.user_id === assignment?.assessor_id);

  return (
    <>
      <p style={{ marginTop: 0 }}>
        {assignment ? (
          <>
            Assigned to <strong>{current?.email ?? assignment.assessor_id}</strong>{" "}
            <span style={{ color: "var(--text-muted)", fontSize: "var(--lf-body-size)" }}>
              since {new Date(assignment.assigned_at).toLocaleDateString()}
            </span>
          </>
        ) : (
          <span style={{ color: "var(--text-muted)" }}>
            No assessor assigned yet. Scoring cannot start until someone owns it.
          </span>
        )}
      </p>
      {canWrite && (
        <div style={{ display: "flex", gap: "0.6rem", alignItems: "center", flexWrap: "wrap" }}>
          <select value={chosen} onChange={(e) => setChosen(e.target.value)} aria-label="Choose assessor">
            <option value="">Choose member…</option>
            {eligible
              .filter((m) => m.user_id !== assignment?.assessor_id)
              .map((m) => (
                <option key={m.user_id} value={m.user_id}>
                  {m.email} ({ROLE_LABEL[m.member_role]})
                </option>
              ))}
          </select>
          <button
            className="btn"
            disabled={chosen === ""}
            onClick={() => {
              onAssign(chosen);
              setChosen("");
            }}
          >
            {assignment ? "Reassign" : "Assign"}
          </button>
        </div>
      )}
      {!canWrite && run.state !== "draft" && run.state !== "ready_for_review" && (
        <p style={{ color: "var(--text-muted)", fontSize: "var(--lf-body-size)", margin: 0 }}>
          Assignment is closed once a run leaves active work.
        </p>
      )}
    </>
  );
}
